import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../../config/supabase";
import { useAuth } from "../../contexts/AuthContext";
import {
    getMyDeliveries,
    subscribeToMyDeliveryUpdates,
    isDeliveryCompleted
} from "../../lib/supabase/my-deliveries";

import {
    FiCalendar, FiUser, FiPhone, FiMapPin, FiAlertCircle, FiEye,
    FiMail, FiPhoneOff, FiClock, FiCheckCircle, FiTruck,
    FiFlag, FiSearch, FiPackage, FiHome, FiLayers, FiBox
} from "react-icons/fi";

const MyDeliveries = () => {
    const { userProfile, company } = useAuth();
    const navigate = useNavigate();
    const { companySlug } = useParams();

    const [deliveries, setDeliveries] = useState([]);
    const [filteredDeliveries, setFilteredDeliveries] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortBy, setSortBy] = useState("newest");
    const [showCompleted, setShowCompleted] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(new Date());

    useEffect(() => {
        if (company?.id && userProfile?.id) {
            fetchMyDeliveries();
        }
    }, [company, userProfile]);

    const fetchMyDeliveries = async () => {
        try {
            const data = await getMyDeliveries(userProfile.id, company.id);
            setDeliveries(data);
            setFilteredDeliveries(data);
            setLastUpdated(new Date());
        } catch (error) {
            console.error("Error fetching my deliveries:", error);
            alert("Error fetching your deliveries");
        }
    };

    // Real-time subscription
    useEffect(() => {
        if (!company?.id) return;

        const channel = subscribeToMyDeliveryUpdates(company.id, (payload) => {
            console.log('Real-time update:', payload);
            fetchMyDeliveries();
        });

        return () => {
            supabase.removeChannel(channel);
        };
    }, [company?.id]);

    // Filter and search logic
    useEffect(() => {
        let filtered = [...deliveries];

        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(
                (delivery) =>
                    delivery.unique_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    delivery.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    delivery.customer_address?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Completion filter
        if (!showCompleted) {
            filtered = filtered.filter(delivery =>
                !delivery.job_deliveries?.every(d => isDeliveryCompleted(d))
            );
        }

        // Sort
        switch (sortBy) {
            case "newest":
                filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                break;
            case "oldest":
                filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                break;
            case "delivery_date":
                filtered.sort((a, b) => {
                    const aDate = a.booking_delivery_date || a.created_at;
                    const bDate = b.booking_delivery_date || b.created_at;
                    return new Date(aDate) - new Date(bDate);
                });
                break;
            case "urgent":
                filtered.sort((a, b) => {
                    const aUrgent = a.urgent_quote || a.urgent_design;
                    const bUrgent = b.urgent_quote || b.urgent_design;
                    return bUrgent - aUrgent;
                });
                break;
            default:
                break;
        }

        setFilteredDeliveries(filtered);
    }, [deliveries, searchTerm, sortBy, showCompleted]);

    const getDeliveryStatusColor = (status) => {
        const statusColors = {
            pending: "bg-amber-50 text-amber-700 border-amber-200",
            in_transit: "bg-blue-50 text-blue-700 border-blue-200",
            delivered: "bg-green-50 text-green-700 border-green-200",
            completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
            cancelled: "bg-red-50 text-red-700 border-red-200"
        };
        return statusColors[status] || "bg-gray-50 text-gray-700 border-gray-200";
    };

    const getDeliveryStatusIcon = (status) => {
        const icons = {
            pending: <FiClock className="w-3.5 h-3.5" />,
            in_transit: <FiTruck className="w-3.5 h-3.5" />,
            delivered: <FiCheckCircle className="w-3.5 h-3.5" />,
            completed: <FiCheckCircle className="w-3.5 h-3.5" />,
            cancelled: <FiAlertCircle className="w-3.5 h-3.5" />
        };
        return icons[status] || <FiClock className="w-3.5 h-3.5" />;
    };

    const getQuoteTypeIcon = (quoteType) => {
        const icons = {
            roof: <FiHome className="w-3 h-3" />,
            wall: <FiLayers className="w-3 h-3" />,
            floor: <FiBox className="w-3 h-3" />,
            timber: <FiPackage className="w-3 h-3" />
        };
        return icons[quoteType] || <FiPackage className="w-3 h-3" />;
    };

    const formatDate = (dateString) => {
        if (!dateString) return "Not set";
        return new Date(dateString).toLocaleDateString("en-AU", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const formatRelativeTime = (date) => {
        if (!date) return 'Unknown time';

        const now = new Date();
        const inputDate = new Date(date);
        const diffMs = now - inputDate;
        const diffMinutes = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMinutes / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffMinutes < 1) return 'just now';
        if (diffMinutes < 60) return `${diffMinutes}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;

        return formatDate(date);
    };

    const handleViewDelivery = (jobId) => {
        navigate(`/${companySlug}/assignment/${jobId}`);
    };

    const FilterButton = ({ label, isSelected, onClick }) => (
        <button
            type="button"
            onClick={onClick}
            className={`px-3 py-1.5 rounded-lg border transition-all duration-200 font-medium text-sm ${isSelected
                ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                }`}
        >
            {label}
        </button>
    );

    // Delivery Items Component
    const DeliveryItems = ({ job }) => {
        const deliveryItems = job.job_deliveries || [];

        return (
            <div className="flex items-center gap-1.5 flex-wrap">
                {deliveryItems.map((delivery) => (
                    <div
                        key={delivery.id}
                        className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 ${isDeliveryCompleted(delivery)
                            ? 'bg-green-100 text-green-800'
                            : delivery.delivery_status === 'in_transit'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}
                        title={`${delivery.quote_type.charAt(0).toUpperCase() + delivery.quote_type.slice(1)} - ${delivery.delivery_status || 'pending'}`}
                    >
                        {getQuoteTypeIcon(delivery.quote_type)}
                        {isDeliveryCompleted(delivery) && <FiCheckCircle className="w-3 h-3" />}
                        <span>{delivery.quote_type.charAt(0).toUpperCase() + delivery.quote_type.slice(1)}</span>
                    </div>
                ))}
            </div>
        );
    };

    const isAllDeliveriesCompleted = (job) => {
        return job.job_deliveries?.every(d => isDeliveryCompleted(d)) || false;
    };

    return (
        <div className="min-h-screen">
            <div className="w-full mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                                <FiTruck className="w-7 h-7 text-blue-600" />
                                My Deliveries
                            </h1>
                            <p className="text-sm text-gray-600 mt-1">
                                Last updated {formatRelativeTime(lastUpdated)}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Filters Card */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
                    {/* Search */}
                    <div className="mb-4">
                        <div className="relative">
                            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                placeholder="Search by Job ID, Customer, or Address..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            />
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="space-y-3">
                        <div className="flex flex-wrap items-center gap-4">
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-700">Sort:</span>
                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                                >
                                    <option value="newest">Newest First</option>
                                    <option value="oldest">Oldest First</option>
                                    <option value="delivery_date">Delivery Date</option>
                                    <option value="urgent">Urgent First</option>
                                </select>
                            </div>

                            {/* Completion Filter */}
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-700">Show:</span>
                                <FilterButton
                                    label="All Deliveries"
                                    isSelected={showCompleted}
                                    onClick={() => setShowCompleted(true)}
                                />
                                <FilterButton
                                    label="Hide Completed"
                                    isSelected={!showCompleted}
                                    onClick={() => setShowCompleted(false)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Results Count */}
                    <div className="mt-4 pt-3 border-t border-gray-200">
                        <span className="text-sm text-gray-600">
                            Showing <span className="font-semibold text-gray-900">{filteredDeliveries.length}</span> of{' '}
                            <span className="font-semibold text-gray-900">{deliveries.length}</span> deliveries
                        </span>
                    </div>
                </div>

                {/* Deliveries List */}
                <div className="space-y-3">
                    {filteredDeliveries.length === 0 ? (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <FiTruck className="w-8 h-8 text-gray-400" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No deliveries found</h3>
                            <p className="text-gray-600">Try adjusting your search or filter criteria</p>
                        </div>
                    ) : (
                        filteredDeliveries.map((job) => (
                            <div
                                key={job.id}
                                onClick={() => handleViewDelivery(job.id)}
                                className={`bg-white rounded-lg shadow-sm border transition-all duration-200 cursor-pointer ${isAllDeliveriesCompleted(job)
                                    ? 'border-green-300 bg-green-50 hover:shadow-md hover:border-green-400'
                                    : 'border-gray-200 hover:shadow-md hover:border-blue-300'
                                    }`}
                            >
                                <div className="p-4">
                                    {/* Header Row */}
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex items-start gap-3 flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <h3 className="font-semibold text-gray-900 text-base">
                                                    {job.unique_id}
                                                </h3>
                                                {(job.urgent_quote || job.urgent_design) && (
                                                    <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-semibold flex items-center gap-1">
                                                        <FiFlag className="w-3 h-3" />
                                                        URGENT
                                                    </span>
                                                )}
                                                {isAllDeliveriesCompleted(job) && (
                                                    <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-semibold flex items-center gap-1">
                                                        <FiCheckCircle className="w-3 h-3" />
                                                        All Completed
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <span className="px-3 py-1 rounded-md text-xs font-medium border flex items-center gap-1.5 whitespace-nowrap bg-blue-50 text-blue-700 border-blue-200">
                                            <FiTruck className="w-3.5 h-3.5" />
                                            DELIVERY
                                        </span>
                                    </div>

                                    {/* Delivery Items */}
                                    <div className="mb-3">
                                        <DeliveryItems job={job} />
                                    </div>

                                    {/* Content Grid */}
                                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                                        {/* Customer Info */}
                                        <div className="md:col-span-3">
                                            <div className="flex items-start gap-2">
                                                <FiUser className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-900 truncate">
                                                        {job.customer_name}
                                                    </p>
                                                    <div className="flex items-center gap-1.5 mt-1">
                                                        {job.customer_number && (
                                                            <a
                                                                href={`tel:${job.customer_number}`}
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                                                            >
                                                                <FiPhone className="w-3 h-3" />
                                                                Call
                                                            </a>
                                                        )}
                                                        {job.customer_mail && (
                                                            <a
                                                                href={`mailto:${job.customer_mail}`}
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="text-xs text-purple-600 hover:text-purple-700 flex items-center gap-1"
                                                            >
                                                                <FiMail className="w-3 h-3" />
                                                                Email
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Delivery Address */}
                                        <div className="md:col-span-4">
                                            <div className="flex items-start gap-2">
                                                <FiMapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-900">
                                                        {job.customer_address || "No address provided"}
                                                    </p>
                                                    {job.customer_address && (
                                                        <a
                                                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(job.customer_address)}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1 mt-1"
                                                        >
                                                            <FiMapPin className="w-3 h-3" />
                                                            Open in Maps
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Delivery Date */}
                                        <div className="md:col-span-2">
                                            <div className="flex items-start gap-2">
                                                <FiCalendar className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-xs text-gray-500">Delivery Date</p>
                                                    <p className="text-sm font-medium text-gray-900">
                                                        {formatDate(job.booking_delivery_date)}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Actions */}
                                        <div className="md:col-span-3 flex items-center justify-end">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleViewDelivery(job.id);
                                                }}
                                                className="px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium flex items-center gap-2"
                                            >
                                                <FiEye className="w-4 h-4" />
                                                View Details
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default MyDeliveries;
